import { FC, FormEvent, ReactNode } from 'react'

import { SUBMIT_BUTTON_TEXT } from '@/app/constants/constants'
import { cn } from '@/app/utils'
import { AppButton } from '@/components/shared/button'
import { AppLoader } from '@/components/shared/loader'

type Props = {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void | Promise<void>
  isPending?: boolean
  children: ReactNode
  header?: ReactNode
  submitLabel?: string
  submitButtonClassName?: string
  className?: string
  contentClassName?: string
  scrollMode?: 'content' | 'form'
  customFooter?: ReactNode
}

const DrawerFormLayout: FC<Props> = ({
  onSubmit,
  isPending = false,
  children,
  header,
  submitLabel = SUBMIT_BUTTON_TEXT,
  submitButtonClassName,
  className,
  contentClassName,
  scrollMode = 'content',
  customFooter
}) => {
  const isFormScroll = scrollMode === 'form'

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (isPending) {
      event.preventDefault()
      return
    }

    onSubmit(event)
  }

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-1 flex-col pb-32 pt-20 lg:py-0',
        className
      )}
    >
      <form
        onSubmit={handleSubmit}
        className={cn(
          'flex h-full min-h-0 flex-1 flex-col',
          isFormScroll && 'overflow-y-auto overscroll-contain'
        )}
      >
        {header}
        <div
          className={cn(
            'min-h-0',
            isFormScroll
              ? 'shrink-0 overflow-visible'
              : 'flex-1 overflow-y-auto overscroll-contain',
            contentClassName
          )}
        >
          {children}
        </div>
        <div
          className={cn(
            'shrink-0',
            !isFormScroll && 'sticky bottom-0 z-20 mt-auto',
            isPending && 'cursor-not-allowed'
          )}
        >
          {customFooter ? (
            <>{customFooter}</>
          ) : (
            <AppButton
              fluid
              size="large"
              type="submit"
              aria-busy={isPending}
              data-testid="form-submit-button-id"
              className={cn(
                isPending && 'pointer-events-none',
                submitButtonClassName
              )}
            >
              {isPending ? <AppLoader noText variant="primary" /> : submitLabel}
            </AppButton>
          )}
        </div>
      </form>
    </div>
  )
}

export { DrawerFormLayout }
